import React, { FC } from 'react';
import { Box, Icon, LinearProgress, Typography, useTheme } from '@mui/material';
import {
  checkPassword,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from '@shared/utils/passwordPolicy';
import { usePasswordStrengthStyle } from './passwordStrength.style';

type PasswordRole = 'visitor' | 'manager';
type PasswordLanguage = 'EN' | 'SE';

interface IPasswordStrengthProps {
  password?: string;
  role?: PasswordRole;
  language?: PasswordLanguage;
  /**
   * Lay the rules out as a wrapping row without the title, and show them only
   * while the password is not accepted yet and the field is in use (focused
   * or not empty). Once valid, only the bar remains. For tight forms.
   */
  inline?: boolean;
  /** Inline only: the password field currently has focus. */
  focused?: boolean;
}

const TEXT = {
  EN: {
    title: 'Your password needs:',
    length: (min: number) => `At least ${min} characters`,
    upper: 'An uppercase letter',
    lower: 'A lowercase letter',
    number: 'A number',
    symbol: 'A symbol, for example ! @ # $ %',
    common: 'This password is too common. Choose something less predictable.',
    tooLong: `Use at most ${PASSWORD_MAX_LENGTH} characters.`,
  },
  SE: {
    title: 'Lösenordet måste ha:',
    length: (min: number) => `Minst ${min} tecken`,
    upper: 'En stor bokstav',
    lower: 'En liten bokstav',
    number: 'En siffra',
    symbol: 'Ett specialtecken, till exempel ! @ # $ %',
    common: 'Lösenordet är för vanligt. Välj något mindre förutsägbart.',
    tooLong: `Använd högst ${PASSWORD_MAX_LENGTH} tecken.`,
  },
};

/**
 * Live checklist and strength bar shown under a "new password" field.
 * Uses the same policy the server enforces (shared/utils/passwordPolicy).
 */
const PasswordStrength: FC<IPasswordStrengthProps> = ({
  password = '',
  role = 'visitor',
  language = 'EN',
  inline = false,
  focused = false,
}) => {
  const theme = useTheme();
  const s = usePasswordStrengthStyle(theme);
  const text = TEXT[language] || TEXT.EN;

  const result = checkPassword(password, role);
  const passed = result.rules.filter((rule) => rule.ok).length;
  const started = password.length > 0;

  // The password is only accepted when every rule is met, so the bar is full
  // and green only then.
  const total = result.rules.length;
  const color = result.valid ? 'success' : 'error';
  const progress = result.valid
    ? 100
    : (Math.min(passed, total - 1) / total) * 100;

  const label = (id: string) =>
    id === 'length'
      ? text.length(PASSWORD_MIN_LENGTH[role])
      : text[id as 'upper' | 'lower' | 'number' | 'symbol'];

  const showList = !inline || (!result.valid && (focused || started));

  return (
    <Box sx={s.root}>
      <LinearProgress
        variant="determinate"
        color={color}
        value={started ? progress : 0}
        sx={s.bar}
      />
      {showList && (
        <>
          {!inline && (
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              {text.title}
            </Typography>
          )}
          <Box
            component="ul"
            sx={inline ? { ...s.list, ...s.listInline } : s.list}
          >
            {result.rules.map((rule) => (
              <Box
                component="li"
                key={rule.id}
                sx={s.item}
                style={{
                  color: rule.ok
                    ? theme.palette.success.main
                    : theme.palette.text.secondary,
                }}
              >
                <Icon sx={s.icon}>
                  {rule.ok ? 'check_circle' : 'radio_button_unchecked'}
                </Icon>
                <Typography variant="caption">{label(rule.id)}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
      {passed === result.rules.length && result.common && (
        <Typography variant="caption" component="div" sx={s.warning}>
          {text.common}
        </Typography>
      )}
      {result.tooLong && (
        <Typography variant="caption" component="div" sx={s.warning}>
          {text.tooLong}
        </Typography>
      )}
    </Box>
  );
};

export default PasswordStrength;
